import {Component, Inject, OnInit} from '@angular/core';
import {CycleService} from '@services/cycle.service';
import {Cycle} from '@services/model/cycle';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {VerificationnoteService} from '@services/verificationnote.service';
import {VerificationResource} from '@services/resource/verification-resource';
import {STARTUP_DESC_LABEL} from '@dashboard/startup/startup.component';
import {ErrorService} from '@services/error.service';
import {UUID} from '@util/types';
import {PRODUCT_DESC_LABEL} from '@dashboard/product-editor/product-editor.component';
import {VerificationnoteResource} from '@services/resource/verificationnote-resource';
import {Router} from '@angular/router';
import {MatIcon} from '@angular/material/icon';
import {MarkantElementsModule} from '@markant/elements-angular';
import {CommonModule} from '@angular/common';
import {MomentPipe} from '@util/pipes/moment-pipe';
import {RouteBuilder} from '@util/route-builder';

export interface ProductPublishData {
  productId: UUID;
}

@Component({
  selector: 'app-product-publish',
  templateUrl: './product-publish.component.html',
  styleUrls: ['./product-publish.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIcon, MarkantElementsModule, MatDialogTitle, MatDialogContent, MatDialogActions, MomentPipe],
})
export class ProductPublishComponent implements OnInit {
  productKeyToName = {
    name: 'Name des Produkts',
    claim: 'Claim',
    description: PRODUCT_DESC_LABEL,
    listed: 'Ist Dein Produkt bereits gelistet?',
    uvp: 'Unverbindliche Preisempfehlung',
    productGroup: 'Warenbereich',
    packingUnit: 'Verpackungseinheit',
    coverImage: 'Produktbild',
    pitchDeck: 'Pitch Deck',
  };

  startupKeyToName = {
    name: 'Name Deines Start-ups',
    zip: 'Postleitzahl',
    city: 'Stadtname',
    street: 'Straßenname',
    phone: 'Telefonnummer',
    countryIso2Code: 'Länderauswahl',
    countryPhoneCode: 'Ländervorwahl',
    description: STARTUP_DESC_LABEL,
    logo: 'Logo Deines Start-ups',
    founded: 'Gründungsjahr',
  };

  cycle: Cycle;
  verificationResource: VerificationResource;

  publishForm = new UntypedFormGroup({
    cycleId: new UntypedFormControl('', [Validators.required]),
  });

  get routes() {
    return {
      profile: RouteBuilder.app().startupDashboard().profile().build(),
      product: RouteBuilder.app().startupDashboard().product().build(),
    };
  }

  constructor(
    private readonly cycleService: CycleService,
    private readonly dialogRef: MatDialogRef<object>,
    private readonly verificationnoteService: VerificationnoteService,
    private readonly errors: ErrorService,
    private readonly router: Router,
    @Inject(MAT_DIALOG_DATA) public data: ProductPublishData,
  ) {}

  ngOnInit() {
    const productId = this.data.productId;
    if (productId) {
      this.verificationnoteService.verifyProduct(productId).subscribe((r: VerificationResource) => {
        return (this.verificationResource = this.postProcessVerfication(r));
      });
    }

    this.cycleService.getAll(true).subscribe((cs) => {
      this.errors.assert(cs.length === 1, 'there should only be one cycle');
      this.cycle = cs[0];
    });
  }

  get verificationFailed(): boolean {
    return this.verificationResource && (this.verificationResource.startup.length > 0 || this.verificationResource.product.length > 0);
  }

  postProcessVerfication(r: VerificationResource): VerificationResource {
    //  Hotfix: VerificationResource.startup may contain mandatory fields
    // 'phone' and 'countryPhoneCode'
    //  which cannot be provided in the UI after initial registration
    // We don't treat these as mandatory in the publish dialog
    return new VerificationResource(r.product, this.postProcessVerficationStartup(r.startup));
  }

  navigateTo(route: string): void {
    this.dialogRef.close(null);
    this.router.navigate([route]);
  }

  postProcessVerficationStartup(startup: VerificationnoteResource[]): VerificationnoteResource[] {
    const updatedResource: VerificationnoteResource[] = [];
    startup.forEach((s) => {
      if (s.propertyPath !== 'phone' && s.propertyPath !== 'countryPhoneCode') {
        updatedResource.push(s);
      }
    });
    return updatedResource;
  }

  publish(): void {
    this.dialogRef.close(this.cycle.uuid);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
